import { defineStore } from 'pinia';
import type { IStoreUserData, IStoreUserDevice, IUserData } from '@coreInterfaces/interface';

export const useUserStore = defineStore('user-store', {
    state: () => {
        return {
            datas: {
                currency: '',
                expires: '',
                newLogin: false,
                info: {},
                loggedIn: false,
                token: ''
            } as IStoreUserData,
            device: {
                currentMode: '',
                darkMode: false,
                dType: '',
                language: '',
                page: '',
                testMode: false,
                type: ''
            } as IStoreUserDevice
        };
    },

    getters: {
        getCurrency: (state) => (): Object => {
            return state.datas.currency;
        },

        getDarkMode: (state) => (): boolean => {
            return state.device.darkMode;
        },

        getDefaultType: (state) => (): string => {
            return state.device.dType || '';
        },

        getDeviceMode: (state) => (): string => {
            return state.device.currentMode;
        },

        getExpires: (state) => (): string => {
            return state.datas.expires;
        },

        getNewLogin: (state) => (): boolean => {
            return state.datas.newLogin;
        },

        getInfo: (state) => (): IUserData => {
            return state.datas.info;
        },

        getInfoByKey:
            (state) =>
            (key: string): any => {
                if (key in state.datas.info) {
                    return state.datas.info[key];
                }

                return null;
            },

        getLanguage: (state) => (): string => {
            return state.device.language;
        },

        getLoggedIn: (state) => (): boolean => {
            return state.datas.loggedIn;
        },

        getPage: (state) => (): string => {
            return state.device.page;
        },

        getTestMode: (state) => (): boolean => {
            return state.device.testMode;
        },

        getToken: (state) => (): string => {
            return state.datas.token;
        },

        getType: (state) => (): string => {
            return state.device.type;
        },

        hasPermission:
            (state) =>
            (key: string): boolean => {
                if ('allowed_modules' in state.datas.info) {
                    return state.datas.info['allowed_modules'].includes(key) || false;
                }

                return false;
            }
    },

    actions: {
        clearUserStore(): void {
            this.setInfo({});
            this.setLoggedIn(false);
            this.setToken('');
            this.setTestMode(false);
        },

        setCurrency(currency: string): void {
            this.datas.currency = currency;
        },

        setDarkMode(darkMode: boolean): void {
            this.device.darkMode = darkMode;
        },

        setDefaultType(dType: string): void {
            this.device.dType = dType;
        },

        setDeviceMode(mode: string): void {
            this.device.currentMode = mode;
        },

        setExpires(expires: string): void {
            this.datas.expires = expires;
        },

        setNewLogin(newLogin: boolean): void {
            this.datas.newLogin = newLogin;
        },

        setInfo(infos: IUserData): void {
            this.datas.info = infos;
        },

        setInfoByKey(key: string, infos: IUserData): void {
            this.datas.info[key] = infos;
        },

        setLanguage(lang: string): void {
            this.device.language = lang;
        },

        setLoggedIn(isLoggedIn: boolean): void {
            this.datas.loggedIn = isLoggedIn;
        },

        setPage(page: string): void {
            this.device.page = page;
        },

        setTestMode(isTestMode: boolean): void {
            this.device.testMode = isTestMode;
        },

        setToken(token: string): void {
            this.datas.token = token;
        },

        setType(type: string): void {
            this.device.type = type;
        }
    }
});
